import React, { useCallback, useRef, useState } from 'react';
import type { FormatOptionLabelMeta, MenuPlacement, Props } from 'react-select';
import type Select from 'react-select/dist/declarations/src/Select';
import { useTheme } from '../../theme';
import { HOT_KEY, useHotKey } from '../HotKeys';
import { Control } from './components/Control/Control';
import { DropdownIndicatorComponent } from './components/DropdownIndicator/DropdownIndicator';
import { ScrollIntoViewMenuList } from './components/MenuList/ScrollIntoViewMenuList';
import { Option } from './components/Option/Option';
import { SingleValue } from './components/SingleValue/SingleValue';
import { StyledSelect } from './view';

export interface SelectorProps extends Props {
    options: any[];
    onChange: (option: any) => void;
    value: any;
    classNamePrefix?: 'react-selector' | 'selector';
    isDisabled?: boolean;
    placeholder?: string;
    isSearchable?: boolean;
    isError?: boolean;
    withoutBg?: boolean;
    width?: string;
    menuHeight?: number;
    menuPlacement?: MenuPlacement;
    cy?: string;
    formatOptionLabel?: (data: any, formatOptionLabelMeta: FormatOptionLabelMeta<any>) => React.ReactNode;
    alwaysScrollIntoView?: boolean;
    onMenuOpen?: () => void;
    onMenuClose?: () => void;
    selectorId?: string;
}

export const Selector = ({
    options,
    onChange,
    value,
    isDisabled,
    placeholder,
    isSearchable = true,
    isError = false,
    withoutBg = false,
    width = '110px',
    menuHeight = 250,
    menuPlacement = 'bottom',
    cy,
    classNamePrefix = 'selector',
    formatOptionLabel,
    alwaysScrollIntoView = false,
    onMenuOpen,
    onMenuClose,
    selectorId = 'selector',
    ...restProps
}: SelectorProps) => {
    const theme = useTheme();
    const ref = useRef<Select>(null);
    const [isMenuOpen, setIsMenuOpen] = useState(false);

    useHotKey(HOT_KEY.ESC, () => ref.current?.blur(), {
        componentId: selectorId,
        enabled: isMenuOpen,
        stopPropagation: true,
        handlerName: 'close-select',
    });

    const handleMenuOpen = useCallback(() => {
        setIsMenuOpen(true);
        onMenuOpen?.();
    }, [onMenuOpen]);

    const handleMenuClose = useCallback(() => {
        setIsMenuOpen(false);
        onMenuClose?.();
    }, [onMenuClose]);

    return (
        <StyledSelect
            ref={ref}
            {...restProps}
            menuHeight={menuHeight}
            isError={isError}
            menuPlacement={menuPlacement}
            isDisabled={isDisabled}
            theme={theme}
            components={{
                DropdownIndicator: options.length > 1 ? DropdownIndicatorComponent : null,
                Option,
                SingleValue,
                Control,
                MenuList: props => (
                    <ScrollIntoViewMenuList
                        {...props}
                        alwaysScrollIntoView={alwaysScrollIntoView}
                    />
                ),
            }}
            isSearchable={isSearchable}
            classNamePrefix={classNamePrefix}
            placeholder={placeholder}
            options={options}
            onChange={onChange}
            value={value}
            withoutBg={withoutBg}
            width={width}
            cy={cy}
            formatOptionLabel={formatOptionLabel}
            onMenuOpen={handleMenuOpen}
            onMenuClose={handleMenuClose}
        />
    );
};
